import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import { FaAngleLeft } from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  Divider,
  Grid,
  Link,
  Paper,
  Badge,
  Button,
} from "@mui/material";

import {
  Person as PersonIcon,
  ShoppingCart as CartIcon,
  Receipt as OrderIcon,
  Favorite as WishlistIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VerifiedUser as VerifiedIcon,
  CheckCircle as ApprovedIcon,
  Pending as PendingIcon,
  Cancel as RejectedIcon,
  LocationOn as LocationOnIcon,
} from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";

// Custom green color palette for consistent theming
const primaryGreen = "#DC3545";
const lightGreen = "#ffe5e5";
const darkGreen = "#DC3545";

function SellerDetails() {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [sellerData, setSellerData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [carts, setCarts] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSellerData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
          toast.error("Authentication token not found. Please log in.");
          navigate("/login");
          return;
        }
        const profileRes = await getWithTokenAPI(
          token,
          `/user/find-particular?_id=${sellerId}`
        );
        if (profileRes.statusCode === 200 && profileRes.data) {
          setSellerData(profileRes.data);
        } else {
          toast.error(profileRes.message || "Failed to fetch seller profile.");
          setSellerData(null);
        }

        // Fetch seller orders
        const ordersRes = await getWithTokenAPI(
          token,
          `/order/find-all?seller_id=${sellerId}`
        );
        setOrders(ordersRes.data?.orders || []);

        // Fetch seller carts
        const cartsRes = await getWithTokenAPI(
          token,
          `/cart/find-all-seller?seller_id=${sellerId}`
        );
        setCarts(cartsRes.data?.Cart || []);

        // Fetch seller wishlists
        const wishlistsRes = await getWithTokenAPI(
          token,
          `/wishlist/find-all?seller_id=${sellerId}`
        );
        setWishlists(wishlistsRes.data?.wishlist || []);
      } catch (error) {
        toast.error("Failed to fetch seller data");
        console.error("Fetch error:", error);
        setSellerData(null); // Ensure data is cleared on error
        setOrders([]);
        setCarts([]);
        setWishlists([]);
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchSellerData();
    }
  }, [sellerId, navigate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress style={{ color: primaryGreen }} />
        <Typography sx={{ ml: 2 }}>Loading seller data...</Typography>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "Approval":
        return "success";
      case "Reject":
        return "error";
      case "Active":
        return "success";
      case "Inactive":
        return "default";
      case "Delivered":
        return "success";
      case "Shipped":
        return "info";
      case "Paid":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Approval":
        return <ApprovedIcon fontSize="small" />;
      case "Pending":
        return <PendingIcon fontSize="small" />;
      case "Reject":
        return <RejectedIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (!sellerData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <ToastContainer position="top-center" autoClose={3000} />
        <Typography variant="h6" color="error">
          Seller details not found or an error occurred.
        </Typography>
        <Button
          variant="contained"
          sx={{
            mt: 2,
            backgroundColor: primaryGreen,
            "&:hover": { backgroundColor: darkGreen },
          }}
          onClick={() => navigate("/seller-management")}
        >
          Back to Seller Management
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />
      <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                color: darkGreen,
                marginBottom: "20px",
              }}
            >
              <FaAngleLeft />
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", width: "150px" }}
                onClick={() => {
                  navigate("/backoffice/SellerManagement");
                }}
              >
                back
              </Typography>
            </Box>
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: "8px",
          border: `1px solid ${lightGreen}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "column" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              alignItems: "center",
              mr: { md: 4 },
              mb: { xs: 3, md: 0 },
            }}
          >
            {sellerData?.profile_image && (
              <Avatar
                src={sellerData.profile_image}
                sx={{
                  width: 120,
                  height: 120,
                  border: `3px solid ${primaryGreen}`,
                  mb: 2,
                }}
                imgProps={{
                  onError: (e) => {
                    e.target.src =
                      "https://placehold.co/150x150/cccccc/333333?text=No+Image";
                  },
                }}
              />
            )}
            <Box sx={{ display: "flex", mt: 1, flexDirection: "column" }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: darkGreen }}
              >
                {sellerData?.name || "Seller"}
              </Typography>

              <Box sx={{ display: "flex", mt: 1 }}>
                <Chip
                  label={sellerData?.Approval_status || "Pending"}
                  color={getStatusColor(sellerData?.Approval_status)}
                  icon={getStatusIcon(sellerData?.Approval_status)}
                  size="small"
                  sx={{ mr: 1 }}
                />
                {sellerData?.otp_verify && (
                  <Chip
                    label="Verified"
                    size="small"
                    sx={{
                      backgroundColor: lightGreen,
                      color: darkGreen,
                      mr: 1,
                    }}
                    icon={
                      <VerifiedIcon
                        fontSize="small"
                        sx={{ color: primaryGreen }}
                      />
                    }
                  />
                )}
                <Chip
                  label={sellerData?.status || "N/A"}
                  color={getStatusColor(sellerData?.status)}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
          <Grid container spacing={2} sx={{ flex: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <EmailIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Email:</strong>{" "}
                  {sellerData?.email ? (
                    <Link href={`mailto:${sellerData.email}`} underline="hover">
                      {sellerData.email}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PhoneIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Phone:</strong>{" "}
                  {sellerData?.phone_number ? (
                    <Link
                      href={`tel:${sellerData.phone_number}`}
                      underline="hover"
                    >
                      {sellerData.phone_number}
                    </Link>
                  ) : (
                    "N/A"
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SupervisorAccountIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Role:</strong> {sellerData?.role || "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ArticleIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>User ID:</strong> {sellerData?._id || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarMonthIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Created:</strong>{" "}
                  {sellerData?.createdAt
                    ? new Date(sellerData.createdAt).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CalendarMonthIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Updated:</strong>{" "}
                  {sellerData?.updatedAt
                    ? new Date(sellerData.updatedAt).toLocaleDateString()
                    : "N/A"}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocalShippingIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Delivery Method:</strong>{" "}
                  {sellerData?.seller_profile?.delivery_method || "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocationOnIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Serving Location:</strong>{" "}
                  {Array.isArray(
                    sellerData?.seller_profile?.serving_location
                  ) && sellerData.seller_profile.serving_location.length > 0
                    ? sellerData.seller_profile.serving_location.join(", ")
                    : "Not Specified"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ArticleIcon
                  sx={{ color: darkGreen, mr: 2, fontSize: "1.2rem" }}
                />
                <Typography variant="body1">
                  <strong>Government ID Proof:</strong>{" "}
                  {sellerData?.seller_profile?.govt_id_proof ? (
                    <Link
                      href={`${sellerData.seller_profile.govt_id_proof}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      View Document
                    </Link>
                  ) : (
                    "Not Available"
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>
      <Box
        sx={{
          width: "100%",
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          bgcolor: lightGreen,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="seller details tabs"
          variant="fullWidth"
          centered
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: primaryGreen,
              height: "4px",
              borderRadius: "4px 4px 0 0",
            },
            "& .MuiTab-root": {
              color: darkGreen,
              fontWeight: "bold",
              textTransform: "none",
              py: 1.5,
              "&.Mui-selected": {
                color: primaryGreen,
                backgroundColor: "white",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                borderBottom: "none",
              },
              "&:hover": {
                backgroundColor: "rgba(0, 178, 7, 0.05)",
              },
            },
          }}
        >
          <Tab
            label="Orders"
            value="orders"
            icon={<OrderIcon />}
            iconPosition="start"
          />
          <Tab
            label="Carts"
            value="carts"
            icon={<CartIcon />}
            iconPosition="start"
          />
          <Tab
            label="Wishlists"
            value="wishlists"
            icon={<WishlistIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>
      <Box sx={{ width: "100%", minHeight: "60vh" }}>
        {activeTab === "orders" && (
          <Box>
            {orders.length > 0 ? (
              <Grid container spacing={3}>
                {orders.map((order) => {
                  const products = order.cart_id?.cart || [];

                  const totalPrice = products.reduce((sum, product) => {
                    return sum + (Number(product.item_total) || 0);
                  }, 0);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={order._id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderTop: `4px solid ${primaryGreen}`,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          transition: "transform 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                          },
                        }}
                      >
                        <CardHeader
                          title={`Order #${
                            order.order_id ||
                            order._id?.substring(0, 8) ||
                            "N/A"
                          }`}
                          subheader={new Date(
                            order.created_at
                          ).toLocaleDateString()}
                          titleTypographyProps={{
                            variant: "subtitle1",
                            fontWeight: "bold",
                            color: darkGreen,
                          }}
                          action={
                            <Chip
                              label={order.order_status || "N/A"}
                              size="small"
                              sx={{
                                backgroundColor:
                                  order.order_status === "Delivered"
                                    ? lightGreen
                                    : order.order_status === "Shipped"
                                    ? "#e3f2fd"
                                    : order.order_status === "Pending"
                                    ? "#fff8e1"
                                    : "#f5f5f5",
                                color:
                                  order.order_status === "Delivered"
                                    ? darkGreen
                                    : order.order_status === "Shipped"
                                    ? "#1565c0"
                                    : order.order_status === "Pending"
                                    ? "#ff8f00"
                                    : "#616161",
                              }}
                            />
                          }
                        />
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <PersonIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              Customer: {order.buyer_id?.name || "N/A"}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 0.5,
                              }}
                            >
                              <EmailIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              {order.buyer_id?.email || "N/A"}
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <LocalShippingIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              Shipping: {order.address_id?.city || "N/A"},{" "}
                              {order.address_id?.state || "N/A"}
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <PaymentIcon
                                fontSize="small"
                                sx={{ mr: 1, color: primaryGreen }}
                              />
                              {order.payment_method || "N/A"} •
                              <Chip
                                label={order.payment_status || "N/A"}
                                size="small"
                                sx={{
                                  ml: 1,
                                  backgroundColor:
                                    order.payment_status === "Paid"
                                      ? lightGreen
                                      : "#fff8e1",
                                  color:
                                    order.payment_status === "Paid"
                                      ? darkGreen
                                      : "#ff8f00",
                                }}
                              />
                            </Typography>
                          </Box>

                          <Divider sx={{ my: 2 }} />
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: darkGreen,
                                fontWeight: "bold",
                                mb: 1,
                              }}
                            >
                              Products:
                            </Typography>
                            {products.length > 0 ? (
                              <Grid container spacing={1}>
                                {products.slice(0, 3).map((item, index) => (
                                  <Grid item xs={4} key={index}>
                                    <Box
                                      sx={{
                                        position: "relative",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Avatar
                                        src={
                                          item.product_id?.product_image?.[0]
                                        }
                                        variant="rounded"
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          border: `1px solid ${primaryGreen}`,
                                          margin: "0 auto",
                                          objectFit: "cover",
                                        }}
                                      >
                                        {!item.product_id
                                          ?.product_image?.[0] && (
                                          <Typography
                                            variant="caption"
                                            sx={{ fontSize: "0.6rem" }}
                                          >
                                            No Image
                                          </Typography>
                                        )}
                                      </Avatar>
                                      <Badge
                                        badgeContent={item.quantity}
                                        sx={{
                                          "& .MuiBadge-badge": {
                                            backgroundColor: primaryGreen,
                                            color: "white",
                                            fontSize: "0.7rem",
                                            minWidth: "20px",
                                            height: "20px",
                                            p: "0 4px",
                                          },
                                          position: "absolute",
                                          top: -8,
                                          right: -8,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          mt: 0.5,
                                          fontWeight: 600,
                                          color: darkGreen,
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 56,
                                        }}
                                        title={item.product_id?.name || "N/A"}
                                      >
                                        {item.product_id?.name || "N/A"}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                                {products.length > 3 && (
                                  <Grid item xs={4}>
                                    <Avatar
                                      sx={{
                                        width: 56,
                                        height: 56,
                                        bgcolor: lightGreen,
                                        color: darkGreen,
                                        border: `1px dashed ${primaryGreen}`,
                                        fontSize: "0.8rem",
                                        margin: "0 auto",
                                      }}
                                    >
                                      +{products.length - 3}
                                    </Avatar>
                                  </Grid>
                                )}
                              </Grid>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No products in this order.
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: lightGreen,
                              textAlign: "right",
                              borderRadius: "8px",
                              mt: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ color: darkGreen, fontWeight: "bold" }}
                            >
                              Total(Ksh): {totalPrice.toFixed(2)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                  mt: 4,
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No orders found.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
        {activeTab === "carts" && (
          <Box>
            {carts.length > 0 ? (
              <Grid container spacing={3}>
                {carts.map((cart) => (
                  <Grid item xs={12} sm={6} md={4} key={cart._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${primaryGreen}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      <CardHeader
                        title={`Cart for ${cart.user_id?.name || "N/A"}`}
                        subheader={new Date(
                          cart.createdAt
                        ).toLocaleDateString()}
                        titleTypographyProps={{
                          variant: "subtitle1",
                          fontWeight: "bold",
                          color: darkGreen,
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <EmailIcon
                              fontSize="small"
                              sx={{ mr: 1, color: primaryGreen }}
                            />
                            Customer Email: {cart.user_id?.email || "N/A"}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: darkGreen,
                              fontWeight: "bold",
                              mb: 1,
                            }}
                          >
                            Products in Cart:
                          </Typography>
                          {cart.cart.length > 0 ? (
                            <Grid container spacing={1}>
                              {cart.cart.slice(0, 3).map((item, index) => (
                                <Grid item xs={4} key={index}>
                                  <Box
                                    sx={{
                                      position: "relative",
                                      textAlign: "center",
                                    }}
                                  >
                                    <Avatar
                                      src={item.product_id?.product_image?.[0]}
                                      variant="rounded"
                                      sx={{
                                        width: 56,
                                        height: 56,
                                        border: `1px solid ${primaryGreen}`,
                                        margin: "0 auto",
                                        objectFit: "cover",
                                      }}
                                    >
                                      {!item.product_id?.product_image?.[0] && (
                                        <Typography
                                          variant="caption"
                                          sx={{ fontSize: "0.6rem" }}
                                        >
                                          No Image
                                        </Typography>
                                      )}
                                    </Avatar>
                                    <Badge
                                      badgeContent={item.quantity}
                                      sx={{
                                        "& .MuiBadge-badge": {
                                          backgroundColor: primaryGreen,
                                          color: "white",
                                          fontSize: "0.7rem",
                                          minWidth: "20px",
                                          height: "20px",
                                          p: "0 4px",
                                        },
                                        position: "absolute",
                                        top: -8,
                                        right: -8,
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        mt: 0.5,
                                        fontWeight: 600,
                                        color: darkGreen,
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: 56,
                                      }}
                                      title={item.product_id?.name || "N/A"}
                                    >
                                      {item.product_id?.name || "N/A"}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                              {cart.cart.length > 3 && (
                                <Grid item xs={4}>
                                  <Avatar
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      bgcolor: lightGreen,
                                      color: darkGreen,
                                      border: `1px dashed ${primaryGreen}`,
                                      fontSize: "0.8rem",
                                      margin: "0 auto",
                                    }}
                                  >
                                    +{cart.cart.length - 3}
                                  </Avatar>
                                </Grid>
                              )}
                            </Grid>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No products in this cart.
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                  mt: 4,
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No carts found.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
        {activeTab === "wishlists" && (
          <Box>
            {wishlists.length > 0 ? (
              <Grid container spacing={3}>
                {wishlists.map((wishlist) => (
                  <Grid item xs={12} sm={6} md={4} key={wishlist._id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `4px solid ${primaryGreen}`,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: `0 8px 20px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      <CardHeader
                        title={`Wishlist for ${
                          wishlist.user_id?.name || "N/A"
                        }`}
                        subheader={new Date(
                          wishlist.createdAt
                        ).toLocaleDateString()}
                        titleTypographyProps={{
                          variant: "subtitle1",
                          fontWeight: "bold",
                          color: darkGreen,
                        }}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <EmailIcon
                              fontSize="small"
                              sx={{ mr: 1, color: primaryGreen }}
                            />
                            Customer Email: {wishlist.user_id?.email || "N/A"}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: darkGreen,
                              fontWeight: "bold",
                              mb: 1,
                            }}
                          >
                            Products in Wishlist:
                          </Typography>
                          {wishlist.products?.length > 0 ? (
                            <Grid container spacing={1}>
                              {wishlist.products
                                .slice(0, 3)
                                .map((item, index) => (
                                  <Grid item xs={4} key={index}>
                                    <Box
                                      sx={{
                                        position: "relative",
                                        textAlign: "center",
                                      }}
                                    >
                                      <Avatar
                                        src={
                                          item.product_id?.product_image?.[0]
                                        }
                                        variant="rounded"
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          border: `1px solid ${primaryGreen}`,
                                          margin: "0 auto",
                                          objectFit: "cover",
                                        }}
                                      >
                                        {!item.product_id
                                          ?.product_image?.[0] && (
                                          <Typography
                                            variant="caption"
                                            sx={{ fontSize: "0.6rem" }}
                                          >
                                            No Image
                                          </Typography>
                                        )}
                                      </Avatar>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          mt: 0.5,
                                          fontWeight: 600,
                                          color: darkGreen,
                                          display: "block",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 56,
                                        }}
                                        title={item.product_id?.name || "N/A"}
                                      >
                                        {item.product_id?.name || "N/A"}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                ))}
                              {wishlist.products.length > 3 && (
                                <Grid item xs={4}>
                                  <Avatar
                                    sx={{
                                      width: 56,
                                      height: 56,
                                      bgcolor: lightGreen,
                                      color: darkGreen,
                                      border: `1px dashed ${primaryGreen}`,
                                      fontSize: "0.8rem",
                                      margin: "0 auto",
                                    }}
                                  >
                                    +{wishlist.products.length - 3}
                                  </Avatar>
                                </Grid>
                              )}
                            </Grid>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No products in this wishlist.
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: "center",
                  border: `1px dashed ${primaryGreen}`,
                  borderRadius: "8px",
                  mt: 4,
                }}
              >
                <Typography variant="h6" sx={{ color: darkGreen }}>
                  No wishlists found.
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default SellerDetails;
